import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";
import { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import TermsAcceptance from "./legal/TermsAcceptance";

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: "APPLIER" | "RECRUITER";
  companyName?: string;
  website?: string;
  about?: string;
}

interface TermsState {
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  acceptCookies: boolean;
}

interface SignupFormProps extends React.ComponentProps<"div"> {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  termsValues: TermsState;
  onTermsChange: (key: keyof TermsState, checked: boolean) => void;
  termsErrors?: Partial<Record<keyof TermsState, string>>;
}

export function SignupForm({
  onSubmit,
  register,
  errors,
  watch,
  setValue,
  termsValues,
  onTermsChange,
  termsErrors = {},
  className,
  ...props
}: SignupFormProps) {
  const role = watch("role");
  const isRecruiter = role === "RECRUITER";
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <Card className={cn("w-full", className)} {...props}>
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">
          Create an account
        </CardTitle>
        <CardDescription>
          Enter your details to get started
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              placeholder="John Doe"
              {...register("name", { required: "Name is required" })}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          {/* Role Toggle Switch */}
          <div className="relative flex w-full p-1 bg-muted rounded-full mb-6">
            <div
              className={cn(
                "absolute h-[calc(100%-8px)] top-1 w-[calc(50%-4px)] bg-background rounded-full shadow-sm transition-all duration-300 ease-in-out",
                isRecruiter ? "left-[calc(50%+2px)] translate-x-0" : "left-1 translate-x-0"
              )}
            />
            <button
              type="button"
              className={cn(
                "relative flex-1 py-2 text-sm font-medium transition-colors z-10",
                !isRecruiter ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"
              )}
              onClick={() => setValue("role", "APPLIER")}
            >
              I'm Looking for a Job
            </button>
            <button
              type="button"
              className={cn(
                "relative flex-1 py-2 text-sm font-medium transition-colors z-10",
                isRecruiter ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"
              )}
              onClick={() => setValue("role", "RECRUITER")}
            >
              I'm Hiring Talent
            </button>
          </div>

          <input
            type="hidden"
            {...register("role")}
            value={role || "APPLIER"}
          />

          {/* Conditional Recruiter Fields */}
          {/* Note: In a real app we'd use watch('role') but here we'll check the checkbox value or use a state */}
          {/* For simplicity with hook-form, we can watch the role field */}
          
          {/* Conditional Recruiter Fields */}
          {isRecruiter && (
            <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  placeholder="Acme Inc."
                  {...register("companyName", { required: isRecruiter ? "Company Name is required" : false })}
                />
                {errors.companyName && (
                  <p className="text-sm text-destructive">{errors.companyName.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="website">Website (Optional)</Label>
                <Input
                  id="website"
                  placeholder="https://example.com"
                  {...register("website")}
                />
              </div>

              </div>
          )}

          {/* Password Fields */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Min 8 characters",
                    },
                  })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/80 dark:text-muted-foreground/70 dark:hover:text-gray-300 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  {...register("confirmPassword", {
                    required: "Please confirm password",
                  })}
                  className="pr-10"
                />
                 <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/80 dark:text-muted-foreground/70 dark:hover:text-gray-300 focus:outline-none"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          <TermsAcceptance
            values={termsValues}
            onChange={onTermsChange}
            errors={termsErrors}
          />

          {/* Submit Button */}
          <Button type="submit" className="w-full">
            Create Account
          </Button>

          {/* Login Link */}
          <div className="text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
